import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home, Siren, Ambulance, BriefcaseMedical, PhoneCall, HeartPulse, Hospital, Watch,
  FileText, CalendarCheck, Bot, House, FlaskConical, Video, Stethoscope, Activity,
  Brain, ClipboardList, UserRound, Building2, Dna, Pill, Sparkles, ScanFace, CalendarDays,
  Baby, Bell, Settings, Gift, LifeBuoy, MessageCircle, BookOpen, Info, ChevronRight,
  ChevronDown, LogOut, ShieldCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuthStore } from "@/store/authStore";
import { useLanguageStore } from "@/store/languageStore";

function initials(name) {
  if (!name) return "U";
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

const MENU = {
  home: { label: "Home", icon: Home, path: "/home" },
  emergency: {
    label: "Emergency Services", icon: Siren, badge: "24/7", badgeColor: "#ef4444",
    sub: [
      { label: "Ambulance Services", icon: Ambulance, path: "/ambulance" },
      { label: "On-Demand Paramedic", icon: BriefcaseMedical, path: "/paramedic" },
      { label: "Emergency Contacts", icon: PhoneCall, path: "/emergency-contacts" },
    ],
  },
  "health-wellness": {
    label: "Health & Wellness", icon: HeartPulse,
    sub: [
      { label: "Health Center", icon: Hospital, path: "/health-center" },
      { label: "Smartwatch Health", icon: Watch, path: "/smartwatch" },
      { label: "Health Records", icon: FileText, path: "/records" },
      { label: "Vital Map", icon: Activity, path: "/vital-map" },
      { label: "Daily Tracker", icon: CalendarCheck, path: "/daily-tracker" },
      { label: "AI Companion", icon: Bot, path: "/health-companion" },
    ],
  },
  "at-home-care": {
    label: "At-Home Care", icon: House,
    sub: [
      { label: "Lab Tests at Home", icon: FlaskConical, path: "/at-home-care/lab-tests" },
      { label: "Teleconsultation", icon: Video, path: "/at-home-care/teleconsultation" },
      { label: "Nursing at Home", icon: Stethoscope, path: "/at-home-care/nursing" },
      { label: "Physiotherapy", icon: Activity, path: "/at-home-care/physiotherapy" },
      { label: "Advanced Care", icon: Hospital, path: "/at-home-care/advanced-care" },
      { label: "Therapy Sessions", icon: Brain, path: "/at-home-care/therapy" },
      { label: "My Bookings", icon: ClipboardList, path: "/at-home-care/my-bookings" },
    ],
  },
  consultations: {
    label: "Consultations", icon: UserRound,
    sub: [
      { label: "Hospital Appointment", icon: Building2, path: "/find-care" },
      { label: "My Consultations", icon: ClipboardList, path: "/consultations" },
    ],
  },
  "ai-tools": {
    label: "AI Health Tools", icon: Dna, badge: "NEW", badgeColor: "#1565C0",
    sub: [
      { label: "Drug Interaction Checker", icon: Pill, path: "/drug-interaction" },
      { label: "AI Second Opinion", icon: Stethoscope, path: "/second-opinion" },
      { label: "Dr Sanjeevani Chat", icon: Sparkles, path: "/assistant" },
    ],
  },
  "ai-doctor": {
    label: "AI Doctor Trata Health", icon: Stethoscope, badge: "NEW", badgeColor: "#0EA5E9",
    sub: [
      { label: "Face Health Scan", icon: ScanFace, path: "/ai-doctor" },
      { label: "Dr Sanjeevani Assistant", icon: Sparkles, path: "/assistant" },
      { label: "Screening History", icon: ClipboardList, path: "/ai-doctor/history" },
    ],
  },
  "booking-services": {
    label: "Booking Services", icon: CalendarDays, badge: "NEW", badgeColor: "#1976D2",
    sub: [
      { label: "Book Doctor Appointment", icon: Stethoscope, path: "/doctors" },
      { label: "Book Lab Test", icon: FlaskConical, path: "/lab-booking" },
      { label: "Prescriptions & Pharmacy", icon: Pill, path: "/medications?tab=prescriptions" },
      { label: "Bookings & Reports", icon: ClipboardList, path: "/bookings-reports" },
    ],
  },
  "mom-baby": { label: "Mom & Baby CareHub", icon: Baby, badge: "NEW", badgeColor: "#E91E63", path: "/mom-baby" },
  notifications: { label: "Notifications", icon: Bell, path: "/notifications" },
  "account-settings": { label: "Account Settings", icon: Settings, path: "/account-settings" },
  "help-support": {
    label: "Help & Support", icon: LifeBuoy,
    sub: [
      { label: "Chat with Support", icon: MessageCircle, path: "/support" },
      { label: "App Guide", icon: BookOpen, path: "/app-guide" },
    ],
  },
  "how-it-works": { label: "How It Works", icon: Info, path: "/how-it-works" },
};

const SECTIONS = [
  { title: "Emergency & Care", ids: ["emergency", "health-wellness", "at-home-care", "consultations"] },
  { title: "AI Health Tools", ids: ["ai-tools", "ai-doctor"] },
  { title: "Bookings & Programs", ids: ["booking-services", "mom-baby"] },
  { title: "Account & More", ids: ["notifications", "account-settings", "help-support", "how-it-works"] },
];

const QUICK_TILES = [
  { label: "Bookings", icon: ClipboardList, path: "/bookings-reports" },
  { label: "Profile", icon: UserRound, path: "/profile" },
  { label: "Rewards", icon: Gift, path: "/rewards" },
];

export default function PatientSideMenu({ open, onOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = useAuthStore((s) => s.displayName());
  const profile = useAuthStore((s) => s.customerProfile);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const [expanded, setExpanded] = useState([]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };
  const toggle = (id) => setExpanded((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = () => {
    onOpenChange(false);
    logout();
    navigate("/", { replace: true });
  };

  const renderItem = (id) => {
    const item = MENU[id];
    if (!item) return null;
    const Icon = item.icon;
    const hasSub = Array.isArray(item.sub) && item.sub.length > 0;
    const isExp = expanded.includes(id);
    const active = item.path ? isActive(item.path) : false;
    return (
      <div key={id}>
        <button
          data-testid={`menu-item-${id}`}
          onClick={() => (hasSub ? toggle(id) : go(item.path))}
          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl transition-colors ${active ? "bg-[#ECFDF5]" : "hover:bg-accent"}`}
        >
          <Icon className="h-5 w-5 shrink-0" style={{ color: active ? "#059669" : "#64748b" }} />
          <span className={`flex-1 text-left text-sm ${active ? "text-[#059669] font-bold" : "text-foreground font-medium"}`}>
            {item.label}
          </span>
          {item.badge && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: item.badgeColor }}>
              {item.badge}
            </span>
          )}
          {hasSub ? (
            isExp ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
          )}
        </button>
        {hasSub && isExp && (
          <div className="mt-1 mb-1.5 ml-2 pl-2 border-l border-border space-y-0.5">
            {item.sub.map((s) => {
              const SIcon = s.icon;
              const sActive = isActive(s.path);
              return (
                <button
                  key={s.path + s.label}
                  data-testid={`menu-sub-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => go(s.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${sActive ? "bg-[#DCFCE7]" : "hover:bg-accent"}`}
                >
                  <SIcon className="h-4 w-4 shrink-0" style={{ color: sActive ? "#059669" : "#94a3b8" }} />
                  <span className={`text-sm ${sActive ? "text-[#059669] font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[88vw] max-w-[320px] p-0 bg-[#F6F8FA] flex flex-col" data-testid="patient-side-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <SheetDescription className="sr-only">Navigate Trata Health services and account</SheetDescription>
        <div className="flex-1 overflow-y-auto px-3 pt-5 pb-3 space-y-3.5">
          {/* Profile card */}
          <button
            onClick={() => go("/profile")}
            data-testid="menu-profile-card"
            className="w-full flex items-center gap-3 bg-card rounded-2xl border border-[#DCFCE7] p-3.5 text-left"
          >
            <div className="h-12 w-12 rounded-full bg-[#059669] flex items-center justify-center text-white font-extrabold text-lg shrink-0">
              {initials(displayName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-extrabold text-foreground truncate">{displayName}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.phone || "View & edit profile"}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>

          {/* Language card */}
          <div className="bg-card rounded-2xl border border-[#EEF2F6] p-3">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-2">Language</p>
            <div className="flex gap-1 rounded-xl border border-border p-1">
              {[{ code: "en", label: "🇬🇧  English" }, { code: "hi", label: "🇮🇳  हिंदी" }].map((l) => (
                <button
                  key={l.code}
                  data-testid={`menu-lang-${l.code}`}
                  onClick={() => setLanguage(l.code)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${language === l.code ? "bg-[#059669] text-white" : "text-muted-foreground"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Quick tiles */}
          <div className="flex gap-2">
            {QUICK_TILES.map((tile) => {
              const TIcon = tile.icon;
              return (
                <button
                  key={tile.label}
                  onClick={() => go(tile.path)}
                  data-testid={`menu-quick-${tile.label.toLowerCase()}`}
                  className="flex-1 bg-card rounded-2xl border border-[#EEF2F6] py-3.5 flex flex-col items-center gap-1.5 hover:bg-accent transition-colors"
                >
                  <TIcon className="h-5 w-5 text-[#059669]" />
                  <span className="text-xs font-semibold text-foreground">{tile.label}</span>
                </button>
              );
            })}
          </div>

          {/* Sections */}
          {SECTIONS.map((sec) => (
            <div key={sec.title}>
              <p className="text-[11px] font-extrabold text-muted-foreground/70 uppercase tracking-wide px-2 mb-1.5">{sec.title}</p>
              <div className="bg-card rounded-2xl border border-[#EEF2F6] p-1.5 space-y-0.5">
                {sec.ids.map(renderItem)}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-border bg-[#f8fafc] px-3 pt-2.5 pb-3">
          <button
            onClick={handleLogout}
            data-testid="menu-logout"
            className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] font-bold text-sm"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
          <div className="flex items-center justify-center gap-2 mt-2.5 text-[10px] text-muted-foreground">
            <button onClick={() => go("/consent")} className="hover:underline">Privacy Policy</button>
            <span>·</span>
            <button onClick={() => go("/consent")} className="hover:underline">Terms of Service</button>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> DPDP Compliant</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
